import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { MenuSearchBar, CategorySelector, Loader, MenuList, ProductPopup, Sidebar } from '@components'
import { useBlockNavigation, useCartRedux, useDashboardRedux, useUserRedux, useOrderRedux } from '@hooks'
import { DINING_METHOD } from '@common'

export default function TablePage() {
    const navigate = useNavigate()
    const {
        isLoading: isDashboardLoading,
        tables,
        fetchMenus,
        selectedProduct,
        setSelectedProduct,
        filteredMenus,
    } = useDashboardRedux()
    const { tableId, diningMethod, targetStallId, clearTargetStallId } = useUserRedux()
    const { isUnsavedChanges } = useCartRedux()
    const { fetchAvailablePromos } = useOrderRedux()
    const menuListRef = useRef(null)
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [isScrollingToStall, setIsScrollingToStall] = useState(false)
    const hasScrolledRef = useRef(false) // Track if we've already scrolled for current targetStallId
    const currentTargetStallIdRef = useRef(null) // Track current targetStallId being processed
    const validTableIds = tables.map(t => t.id)

    const handleSidebarToggle = () => {
        setIsSidebarOpen(!isSidebarOpen)
    }

    useEffect(() => {
        fetchMenus()
        fetchAvailablePromos()
        // eslint-disable-next-line
    }, [])

    // Auto scroll to target stall when menus are loaded
    useEffect(() => {
        // Only proceed if we have targetStallId, menus are loaded, and dashboard is not loading
        if (targetStallId && filteredMenus.length > 0 && !isDashboardLoading) {
            // Prevent duplicate execution for the same targetStallId
            // Only skip if we've already scrolled for this targetStallId
            if (currentTargetStallIdRef.current === targetStallId && hasScrolledRef.current) {
                return
            }

            // Reset flags for new targetStallId
            if (currentTargetStallIdRef.current !== targetStallId) {
                hasScrolledRef.current = false
                currentTargetStallIdRef.current = targetStallId
            }

            setIsScrollingToStall(true)

            // Find the matching section from filteredMenus first
            const matchingSection = filteredMenus.find(
                section => section.stallId === targetStallId || section.stallId === Number(targetStallId)
            )

            // Function to find the target section element
            const findTargetSection = () => {
                const stallIdStr = String(targetStallId)
                const stallIdNum = Number(targetStallId)

                // Try by stallId (number and string)
                let section =
                    document.querySelector(`[data-stall-id="${targetStallId}"]`) ||
                    document.querySelector(`[data-stall-id="${stallIdStr}"]`) ||
                    document.querySelector(`[data-stall-id="${stallIdNum}"]`)

                // If not found by stallId, try by category name (fallback)
                if (!section && matchingSection) {
                    section = document.querySelector(`[data-category="${matchingSection.category}"]`)
                }

                return section
            }

            // Function to wait for element to be ready (no image loading wait needed)
            const waitForElementReady = (section, callback) => {
                let lastTop = 0
                let stableCount = 0
                const requiredStableChecks = 2 // Element position must be stable for 2 consecutive checks
                let checkCount = 0
                const maxChecks = 10 // Maximum 10 checks (about 200ms)

                const checkStability = () => {
                    checkCount++
                    const rect = section.getBoundingClientRect()
                    const currentTop = rect.top

                    // Check if element has dimensions
                    if (rect.height > 0 && rect.width > 0) {
                        // Check if position is stable (not changing)
                        if (Math.abs(currentTop - lastTop) < 1) {
                            stableCount++
                            if (stableCount >= requiredStableChecks) {
                                // Element position is stable, safe to scroll
                                callback(section)
                                return
                            }
                        } else {
                            // Position changed, reset stable count
                            stableCount = 0
                            lastTop = currentTop
                        }
                    }

                    // Continue checking if not stable yet
                    if (checkCount < maxChecks) {
                        requestAnimationFrame(checkStability)
                    } else {
                        // Max checks reached, scroll anyway
                        callback(section)
                    }
                }

                // Start checking after a short delay to allow initial render
                requestAnimationFrame(checkStability)
            }

            // Function to scroll to the target section
            const scrollToTargetSection = section => {
                // Prevent duplicate scroll execution
                if (hasScrolledRef.current) {
                    return
                }

                // Mark as scrolled to prevent duplicate execution
                hasScrolledRef.current = true

                // Use requestAnimationFrame to ensure we get the latest position
                requestAnimationFrame(() => {
                    // Get navbar height for offset
                    const navbar = document.querySelector('nav')
                    const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0

                    // Get the current scroll position
                    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop

                    // Get element position
                    const rect = section.getBoundingClientRect()
                    const elementTop = rect.top + currentScrollTop

                    // Calculate target position with offset
                    const extraSpacing = 20
                    const offsetPosition = elementTop - navbarHeight - extraSpacing

                    // Scroll to position
                    window.scrollTo({
                        top: Math.max(0, offsetPosition),
                        behavior: 'smooth',
                    })

                    // After smooth scroll completes, verify and adjust if needed
                    setTimeout(() => {
                        // Re-check the element position after scroll
                        const newRect = section.getBoundingClientRect()
                        const newScrollTop = window.pageYOffset || document.documentElement.scrollTop
                        const elementTopAfterScroll = newRect.top + newScrollTop
                        const targetPosition = elementTopAfterScroll - navbarHeight - extraSpacing

                        // Check if we're close enough (within 50px)
                        const scrollDifference = Math.abs(newScrollTop - targetPosition)
                        if (scrollDifference > 50) {
                            // Not at the right position, scroll again with updated position
                            window.scrollTo({
                                top: Math.max(0, targetPosition),
                                behavior: 'smooth',
                            })

                            // One more check after second scroll
                            setTimeout(() => {
                                clearTargetStallId()
                                setIsScrollingToStall(false)
                            }, 600)
                        } else {
                            // Position is correct
                            clearTargetStallId()
                            setIsScrollingToStall(false)
                        }
                    }, 800) // Wait for smooth scroll to complete (usually ~500ms)
                })
            }

            // Use MutationObserver to watch for DOM changes
            const observer = new MutationObserver(() => {
                // Skip if already scrolled
                if (hasScrolledRef.current) {
                    return
                }

                const section = findTargetSection()
                if (section) {
                    observer.disconnect()
                    // Wait for element to be ready before scrolling
                    waitForElementReady(section, scrollToTargetSection)
                }
            })

            // Start observing the menuListRef container
            if (menuListRef.current) {
                observer.observe(menuListRef.current, {
                    childList: true,
                    subtree: true,
                })
            }

            // Try to find and scroll
            let attempts = 0
            const maxAttempts = 30
            let timeoutId = null

            const tryScroll = () => {
                // Skip if already scrolled
                if (hasScrolledRef.current) {
                    if (timeoutId) clearTimeout(timeoutId)
                    observer.disconnect()
                    return
                }

                attempts++
                const section = findTargetSection()

                if (section) {
                    observer.disconnect()
                    if (timeoutId) clearTimeout(timeoutId)
                    // Wait for element to be ready before scrolling
                    waitForElementReady(section, scrollToTargetSection)
                    return
                }

                if (attempts < maxAttempts) {
                    timeoutId = setTimeout(tryScroll, 100)
                } else {
                    // Element not found after max attempts
                    observer.disconnect()
                    clearTargetStallId()
                    setIsScrollingToStall(false)
                }
            }

            // Start trying after a short delay
            timeoutId = setTimeout(() => {
                tryScroll()
            }, 200)

            // Cleanup
            return () => {
                clearTimeout(timeoutId)
                observer.disconnect()
                setIsScrollingToStall(false)
            }
        } else if (!targetStallId) {
            // Clear loading state if no targetStallId
            setIsScrollingToStall(false)
            hasScrolledRef.current = false
            currentTargetStallIdRef.current = null
        }
        // eslint-disable-next-line
    }, [targetStallId, filteredMenus, isDashboardLoading])

    useEffect(() => {
        const isDineIn = diningMethod.value === DINING_METHOD.DineIn

        if (isDineIn && (!tableId || !validTableIds.includes(tableId.value))) {
            navigate('/', { replace: true })
        }
    }, [tableId, diningMethod, validTableIds, navigate])

    const NavigationBlocker = useBlockNavigation(isUnsavedChanges)

    return (
        <div>
            {(isDashboardLoading || isScrollingToStall) && <Loader />}
            {NavigationBlocker}
            {/* <BannerCarousel items={BANNER_ITEMS} />
            <div className="table-page-header">
                <h2>Menu</h2>
                <MenuDisplayTypeSwitch />
                <MenuSearchBar />
                </div> */}
            <div className="d-flex justify-content-center mt-3 px-3">
                <MenuSearchBar />
            </div>

            <CategorySelector menuListRef={menuListRef} onSidebarToggle={handleSidebarToggle} />
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            <div ref={menuListRef}>
                <MenuList />
            </div>
            <ProductPopup isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} product={selectedProduct} />
        </div>
    )
}
